import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, Button, Input, Select, Label, Modal, Spinner, EmptyState, usePagination, Pagination } from "@/components/UI";
import { Plus, Users, Pencil, Trash2 } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", role: "employee", job_title: "" };
const ROLE_LABEL = { admin: "Admin / PM", employee: "Employee", internship: "Internship" };

export default function Employees() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "employee",
      job_title: u.job_title || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User diperbarui");
      } else {
        await api.post("/users", payload);
        toast.success("User dibuat");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menyimpan user");
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus user "${u.name}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User dihapus");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menghapus user");
    }
  };

  const pg = usePagination(users, 15);

  if (!users) return <Spinner />;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Employees</h1>
          <p className="text-sm text-slate-400">Kelola admin dan employee tim.</p>
        </div>
        <Button onClick={openCreate} data-testid="create-employee-button"><Plus size={16} /> Tambah User</Button>
      </div>

      {users.length === 0 ? (
        <EmptyState icon={Users} title="Belum ada user" subtitle="Tambahkan employee atau admin pertama." />
      ) : (
        <Card className="overflow-hidden" data-testid="employees-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/60 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Nama</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Job Title</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {pg.paged.map((u) => (
                  <tr key={u.id} className="border-b border-slate-800/60 last:border-0" data-testid={`employee-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-200">{u.name}</td>
                    <td className="px-4 py-3 text-slate-400">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                        u.role === 'admin' ? 'bg-slate-500/15 text-slate-300' : 'bg-slate-800 text-slate-300'
                      }`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-400">{u.job_title || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(u)} className="rounded p-1.5 text-slate-500 hover:bg-slate-500/10 hover:text-slate-400" data-testid={`edit-employee-${u.id}`} title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => remove(u)} className="rounded p-1.5 text-slate-500 hover:bg-rose-500/10 hover:text-rose-400" data-testid={`delete-employee-${u.id}`} title="Hapus">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} perPage={pg.perPage} onChange={pg.setPage} />
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit User" : "Tambah User"}
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
          <Button onClick={save} disabled={saving || !form.name || !form.email} data-testid="employee-save">{saving ? "..." : editing ? "Simpan" : "Buat"}</Button></>}>
        <div className="space-y-4">
          <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-email" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="employee-role">
                <option value="employee">Employee</option>
                <option value="internship">Internship</option>
                <option value="admin">Admin / PM</option>
              </Select>
            </div>
          </div>
          <div>
            <Label>Job Title</Label>
            <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="employee-job-title" />
          </div>
          <div>
            <Label>{editing ? "Password (kosongkan jika tidak diubah)" : "Password"}</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="employee-password" />
          </div>
        </div>
      </Modal>
    </div>
  );
}
